import React from 'react';
import { createAsyncInputComponent, useAsyncInput } from './AsyncInput';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';

// Create the MaintainerTeamsInput component using the factory
export const MaintainerTeamsInput = createAsyncInputComponent('MaintainerTeamsInput', {
    getPlaceholder: ({ disabled }: { disabled?: boolean }) => 
        disabled ? "Service name is required" : "Enter Maintainer Teams (comma separated)",
    getAriaLabel: () => "Maintainer Teams (comma separated)",
    getValidated: ({ error, isValidating }: { error?: string; isValidating?: boolean }) => 
        error ? 'error' : (isValidating ? 'warning' : 'default')
});

// Hook for managing maintainer teams state and validation
export const useMaintainerTeamsInput = (organization?: string) => {
  const getToken = useAccessToken();
  // New state to track if the team has been explicitly verified by the API
const [lastVerifiedValue, setLastVerifiedValue] = React.useState<string | null>(null);

    const validateCommaSeparatedAlphaNumeric = (value: string): boolean => {
        if (!value.trim()) return false;
        const items = value.split(',').map(item => item.trim());
        const alphaNumericRegex = /^[a-zA-Z0-9\s-_]+$/;
        return items.every(item => item.length > 0 && alphaNumericRegex.test(item));
    };

    const syncValidator = (value: string): string => {
        if (!value.trim()) return 'Maintainer teams is required';
        if (!validateCommaSeparatedAlphaNumeric(value)) {
            return 'Maintainer teams must be comma-separated alphanumeric values';
        }
        
        // Check for duplicate values
        const items = value.split(',').map(item => item.trim());
        const uniqueItems = new Set(items);
        if (items.length !== uniqueItems.size) {
            return 'Duplicate team names are not allowed';
        }
        
        return '';
    };

    const asyncValidator = async (value: string): Promise<string> => {
        if (!organization) {
            return '';
        }

        try {
            const url = `/api/github/teams?organization=${encodeURIComponent(organization)}&name=${encodeURIComponent(value)}`;
            const validationResults = await fetchApi<Array<{ name: string; isValid: boolean }>>(url, getToken);
            
            const invalidTeams = validationResults
                .filter(result => !result.isValid)
                .map(result => result.name);
            
            if (invalidTeams.length > 0) {
                return `Team name(s): ${invalidTeams.join(',')} not valid.`;
                setLastVerifiedValue(null);
            }
            setLastVerifiedValue(value);
            
            return '';
        } catch (error) {
            setLastVerifiedValue(null);
            console.error('Error validating team existence:', error);
            return 'Failed to validate team existence. Please try again.';
        }
    };

    const asyncInput = useAsyncInput({
        required: true,
        syncValidator,
        asyncValidator,
        dependencies: [organization],
        requiredMessage: 'Maintainer teams is required'
    });

    // Clear async validation errors when organization changes (but not on initial load)
    const previousOrganization = React.useRef<string | undefined>(organization);
    React.useEffect(() => {
        if (previousOrganization.current !== undefined && 
            previousOrganization.current !== organization && 
            asyncInput.error && 
            asyncInput.error.includes('not valid.')) {
            asyncInput.setError('');
        }
        previousOrganization.current = organization;
    }, [organization, asyncInput.error, asyncInput.setError]);

    const isVerified = !!lastVerifiedValue && lastVerifiedValue === asyncInput.value;

    return {
        maintainerTeams: asyncInput.value,
        maintainerTeamsError: asyncInput.error,
        isValidating: asyncInput.isValidating,
        handleMaintainerTeamsChange: asyncInput.handleChange,
        handleMaintainerTeamsBlur: asyncInput.handleBlur,
        validateMaintainerTeams: asyncInput.validate,
        setMaintainerTeamsError: asyncInput.setError,
        isValid: asyncInput.isValid,
        isVerified
    };
};
